import { useCallback } from 'react';
import { FilterToolbarEmbedded } from 'src/components/FilterToolbarEmbedded';
import {
  commissionMandatoryFiltersToShow,
  useCommissionFilters,
} from 'src/components/MainFilterBar/useCommissionFilters';
import {
  costOfGoodsSoldReportMandatoryFiltersToShow,
  useCostOfGoodsSoldReportFilters,
} from 'src/components/MainFilterBar/useCostOfGoodsSoldReportFilter';
import { listingsMandatoryFiltersToShow } from 'src/components/MainFilterBar/useListingFilters';
import { useListingReportFilters } from 'src/components/MainFilterBar/useListingReportFilters';
import { purchaseMandatoryFiltersToShow } from 'src/components/MainFilterBar/usePurchaseFilters';
import {
  purchaseOrderLineItemMandatoryFiltersToShow,
  usePurchaseOrderLineItemReportFilters,
} from 'src/components/MainFilterBar/usePurchaseOrderLineItemReportFilters';
import {
  soldTicketReportMandatoryFiltersToShow,
  usePurchaseReportFilters,
} from 'src/components/MainFilterBar/usePurchaseReportFilters';
import {
  saleCostAdjustmentsReportMandatoryFiltersToShow,
  useSaleCostAdjustmentsReportFilters,
} from 'src/components/MainFilterBar/useSaleCostAdjustmentsReportFilter';
import {
  saleLineItemReportMandatoryFiltersToShow,
  useSaleLineItemReportFilters,
} from 'src/components/MainFilterBar/useSaleLineItemReportFilters';
import {
  saleReportMandatoryFiltersToShow,
  useSaleReportFilters,
} from 'src/components/MainFilterBar/useSaleReportFilters';
import { useSoldTicketReportFilters } from 'src/components/MainFilterBar/useSoldTicketReportFilters';
import {
  ticketGroupReportMandatoryFiltersToShow,
  useTicketGroupReportFilters,
} from 'src/components/MainFilterBar/useTicketGroupReportFilters';
import { useAppContext } from 'src/contexts/AppContext';
import { CatalogDataContextV2Provider } from 'src/contexts/CatalogDataContext/CatalogDataContextV2';
import { Stack } from 'src/core/ui';
import {
  DefaultCommissionQuery,
  DefaultReportV2CostOfGoodsSoldQuery,
  DefaultReportV2ListingQuery,
  DefaultReportV2PurchaseOrderLineItemQuery,
  DefaultReportV2PurchaseQuery,
  DefaultReportV2SaleCostAdjustmentsQuery,
  DefaultReportV2SaleLineItemQuery,
  DefaultReportV2SaleQuery,
  DefaultReportV2SoldTicketQuery,
  DefaultReportV2TicketGroupQuery,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import {
  getCatalogCountForReport,
  getEventsWithNonZeroCountsReporting,
  ReportTypesV2,
} from 'src/utils/reportsUtils';
import {
  ActionOutboxEntityType,
  EventWithData,
  ReportV2Client,
  UIReportCommissionQuery,
  UIReportV2CostOfGoodsSoldQuery,
  UIReportV2ListingQuery,
  UIReportV2PurchaseOrderLineItemQuery,
  UIReportV2PurchaseQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SaleTotalCostAdjustmentsQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
} from 'src/WebApiController';

import {
  ReportFilterInputProps,
  useReportBuilderFilterInputV2,
} from './useReportBuilderFilterInputV2';

export function ReportBuilderFilterInputV2({
  reportType,
  direction,
  ...filterInputProps
}: {
  reportType: ReportTypesV2;
} & ReportFilterInputProps) {
  const reportTypeForCatalog =
    reportType === ReportTypesV2.InventoryV2 ||
    reportType === ReportTypesV2.SaleV2 ||
    reportType === ReportTypesV2.SoldTicket ||
    reportType === ReportTypesV2.SaleLineItem ||
    reportType === ReportTypesV2.TicketGroup
      ? reportType
      : undefined;

  const { activeAccountWebClientConfig } = useAppContext();

  const getCatalogCountForReportWrapper = useCallback(
    (filterQuery: unknown) => {
      if (!reportTypeForCatalog) {
        return Promise.resolve({} as never);
      }
      const client = new ReportV2Client(activeAccountWebClientConfig);
      return getCatalogCountForReport(
        client,
        filterQuery,
        reportTypeForCatalog
      );
    },
    [activeAccountWebClientConfig, reportTypeForCatalog]
  );

  const getEventsWithNonZeroCountsReportingWrapper = useCallback(
    (
      _entityType: ActionOutboxEntityType,
      data?: EventWithData[] | null,
      requireEventNotNull?: boolean
    ) => {
      if (!reportTypeForCatalog) {
        return [];
      }
      return getEventsWithNonZeroCountsReporting(
        data,
        requireEventNotNull,
        reportTypeForCatalog
      );
    },
    [reportTypeForCatalog]
  );

  return (
    <CatalogDataContextV2Provider
      queryKey={`getCatalogForReport${reportTypeForCatalog}`}
      entityType={
        reportTypeForCatalog === ReportTypesV2.InventoryV2
          ? ActionOutboxEntityType.Listing
          : reportTypeForCatalog === ReportTypesV2.TicketGroup
            ? ActionOutboxEntityType.Purchase
            : ActionOutboxEntityType.Sale
      }
      getCatalogCountsCustom={getCatalogCountForReportWrapper}
      getEventsWithNonZeroCountsCustom={
        getEventsWithNonZeroCountsReportingWrapper
      }
      transformEventData={transformData}
      disabled={!reportTypeForCatalog}
    >
      <Stack
        direction={direction}
        alignItems={direction === 'column' ? 'start' : 'center'}
        gap="l"
        style={{
          flexGrow: '1',
        }}
      >
        {reportType === ReportTypesV2.InventoryV2 ? (
          <ListingFilterInput direction={direction} {...filterInputProps} />
        ) : reportType === ReportTypesV2.Commission ? (
          <CommissionFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.Purchase ? (
          <PurchaseFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SoldTicket ? (
          <SoldTicketFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SaleLineItem ? (
          <SaleLineItemFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.PurchaseOrderLineItem ? (
          <PurchaseLineItemFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.CostOfGoodsSold ? (
          <CostOfGoodsSoldFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.SaleCostAdjustments ? (
          <SaleCostAdjustmentsFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : reportType === ReportTypesV2.TicketGroup ? (
          <TicketGroupFilterInput
            direction={direction}
            performerVenueFilter={undefined}
            {...filterInputProps}
          />
        ) : (
          <SaleFilterInput direction={direction} {...filterInputProps} />
        )}
      </Stack>
    </CatalogDataContextV2Provider>
  );
}

const ListingFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useListingReportFilters({ isHorizontalLayout: true });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2ListingQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: listingsMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2ListingQuery) ??
        DefaultReportV2ListingQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleReportFilters({ isHorizontalLayout: true });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: saleReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2SaleQuery) ?? DefaultReportV2SaleQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const CommissionFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useCommissionFilters();

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportCommissionQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: commissionMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportCommissionQuery) ??
        DefaultCommissionQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleLineItemFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleLineItemReportFilters({
    isHorizontalLayout: true,
  });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleLineItemQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: saleLineItemReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2SaleLineItemQuery) ??
        DefaultReportV2SaleLineItemQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const PurchaseFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = usePurchaseReportFilters({ isHorizontalLayout: true });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2PurchaseQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: purchaseMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2PurchaseQuery) ??
        DefaultReportV2PurchaseQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const PurchaseLineItemFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = usePurchaseOrderLineItemReportFilters({
    isHorizontalLayout: true,
  });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2PurchaseOrderLineItemQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: purchaseOrderLineItemMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2PurchaseOrderLineItemQuery) ??
        DefaultReportV2PurchaseOrderLineItemQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const CostOfGoodsSoldFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useCostOfGoodsSoldReportFilters({
    isHorizontalLayout: true,
  });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2CostOfGoodsSoldQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: costOfGoodsSoldReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2CostOfGoodsSoldQuery) ??
        DefaultReportV2CostOfGoodsSoldQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SaleCostAdjustmentsFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSaleCostAdjustmentsReportFilters({
    isHorizontalLayout: true,
  });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SaleTotalCostAdjustmentsQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: saleCostAdjustmentsReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2SaleTotalCostAdjustmentsQuery) ??
        DefaultReportV2SaleCostAdjustmentsQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const SoldTicketFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useSoldTicketReportFilters({ isHorizontalLayout: true });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2SoldTicketQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: soldTicketReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2SoldTicketQuery) ??
        DefaultReportV2SoldTicketQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};

const TicketGroupFilterInput = (props: ReportFilterInputProps) => {
  const { filters } = useTicketGroupReportFilters({ isHorizontalLayout: true });

  const { defaultQuery, ...rest } = props;

  const { filterToolbarProps } =
    useReportBuilderFilterInputV2<UIReportV2TicketGroupQuery>({
      ...rest,
      filters,
      mandatoryFiltersToShow: ticketGroupReportMandatoryFiltersToShow,
      defaultQuery:
        (props.defaultQuery as UIReportV2TicketGroupQuery) ??
        DefaultReportV2TicketGroupQuery,
    });

  return <FilterToolbarEmbedded {...filterToolbarProps} />;
};
